import FastForward from "@mui/icons-material/FastForward";
import FastRewind from "@mui/icons-material/FastRewind";
import SkipNext from "@mui/icons-material/SkipNext";
import SkipPrevious from "@mui/icons-material/SkipPrevious";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import DragIndicator from "@mui/icons-material/DragIndicator";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useTenantTranslation } from "hooks/formatters";
import { PLAYBACK_SPEEDS } from "../constants";
import { ReactNode } from "react";
import { Dayjs } from "dayjs";
import { motion } from "framer-motion";

interface PlaybackControlsProps {
  isPlaying: boolean;
  playbackRate: number;
  onTogglePlayPause: () => void;
  onSkipBackward: () => void;
  onSkipForward: () => void;
  onSpeedChange: (speed: number) => void;
  isRecording?: boolean;
  focusedEvent?: ScrapEyesEvent;
  actionButtons?: ReactNode;
  centerActionButtons?: ReactNode;
  videoStart?: Dayjs;
  showDraggableEventWindow?: boolean;
  onStepBackward?: () => void;
  onStepForward?: () => void;
}

export const PlaybackControls = ({
  isPlaying,
  playbackRate,
  onTogglePlayPause,
  onSkipBackward,
  onSkipForward,
  onSpeedChange,
  isRecording,
  focusedEvent,
  actionButtons,
  centerActionButtons,
  videoStart,
  showDraggableEventWindow,
  onStepBackward,
  onStepForward,
}: PlaybackControlsProps) => {
  const { t } = useTenantTranslation();

  if (focusedEvent && !videoStart) {
    throw new Error("Video start is required when focused event is present");
  }

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
        {onStepBackward ? (
          <IconButton onClick={onStepBackward} size="small">
            <SkipPrevious />
          </IconButton>
        ) : null}
        <IconButton onClick={onSkipBackward} size="small">
          <FastRewind />
        </IconButton>
        <IconButton
          onClick={onTogglePlayPause}
          aria-label={t(isPlaying ? "pause" : "play")}
          size="large"
          sx={{
            bgcolor: "primary.main",
            color: "white",
            "&:hover": {
              bgcolor: "primary.dark",
            },
          }}
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>
        <IconButton onClick={onSkipForward} size="small">
          <FastForward />
        </IconButton>
        {onStepForward ? (
          <IconButton onClick={onStepForward} size="small">
            <SkipNext />
          </IconButton>
        ) : null}
        <FormControl size="small" sx={{ minWidth: 80, ml: 2 }}>
          <InputLabel>{t("speed")}</InputLabel>
          <Select
            value={playbackRate}
            onChange={(e) => {
              onSpeedChange(e.target.value);
            }}
            label={t("speed")}
          >
            {PLAYBACK_SPEEDS.map((speed) => (
              <MenuItem key={speed} value={speed}>
                {speed}
                {`x`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {showDraggableEventWindow && videoStart ? (
        <DraggableEventWindow event={focusedEvent} videoStart={videoStart} />
      ) : null}

      {centerActionButtons}

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ flex: 1 }}
      >
        {isRecording ? (
          <Chip
            icon={<FiberManualRecordIcon />}
            label={t("recording")}
            color="error"
            size="small"
            sx={{
              animation: "pulse 1.5s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.6 },
              },
            }}
          />
        ) : null}
        {actionButtons}
      </Stack>
    </Stack>
  );
};

const formatCountdown = (seconds: number): string => {
  if (seconds < 1) return "<1s";
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const DraggableEventWindow = ({
  event,
  videoStart,
}: {
  event: ScrapEyesEvent | undefined;
  videoStart: Dayjs;
}) => {
  const state = useVideoControlState();
  const currentAbsoluteTime = videoStart.add(state.currentTime, "second");
  const timeUntilStart = event?.start.diff(currentAbsoluteTime, "second");

  return (
    <motion.div
      drag
      dragMomentum={false}
      dragElastic={0}
      style={{
        cursor: "grab",
        zIndex: 1000,
      }}
      whileDrag={{ cursor: "grabbing" }}
    >
      <Paper
        elevation={0}
        sx={{
          minWidth: 200,
          maxWidth: 400,
          bgcolor: "rgba(255, 255, 255, 0.3)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(25, 118, 210, 0.2)",
            py: 0.5,
            px: 1,
          }}
        >
          <DragIndicator sx={{ fontSize: 18, opacity: 0.5 }} />
        </Box>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          p={2}
          justifyContent="center"
        >
          {timeUntilStart !== undefined && (
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: timeUntilStart < 5 ? "error.main" : "primary.main",
                minWidth: "70px",
                textAlign: "right",
              }}
            >
              {formatCountdown(timeUntilStart)}
            </Typography>
          )}
          <Stack spacing={0.5} alignItems="flex-start">
            {event ? (
              <>
                {event.pairId !== null && event.material ? (
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {`${event.eventType === "lift" ? "↑" : "↓"} ${
                      event.material
                    }`}{" "}
                    ({event.areaLabel})
                  </Typography>
                ) : null}
                {event.comment ? (
                  <Typography
                    variant="body2"
                    sx={{
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    {event.comment}
                  </Typography>
                ) : null}
              </>
            ) : null}
          </Stack>
        </Stack>
      </Paper>
    </motion.div>
  );
};
